import jsPDF from "jspdf";
import { utils, writeFile } from "xlsx";
import { formatNumber } from "./format-number";

interface ExportData {
	headers: string[];
	rows: (string | number | null | undefined)[][];
	filename: string;
}

/**
 * Export data to CSV format
 */
export const exportToCSV = ({ headers, rows, filename }: ExportData) => {
	// Create CSV content
	const csvContent = [
		headers.join(","),
		...rows.map((row) =>
			row
				.map((cell) => {
					// Escape quotes and wrap in quotes if contains comma or quotes
					const cellStr = String(cell || "");
					if (cellStr.includes(",") || cellStr.includes('"') || cellStr.includes("\n")) {
						return `"${cellStr.replace(/"/g, '""')}"`;
					}
					return cellStr;
				})
				.join(","),
		),
	].join("\n");

	// Create blob and download
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const link = document.createElement("a");
	const url = URL.createObjectURL(blob);

	link.setAttribute("href", url);
	link.setAttribute("download", `${filename}.csv`);
	link.style.visibility = "hidden";

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Export data to XLSX format
 */
export const exportToXLSX = ({ headers, rows, filename }: ExportData) => {
	// Create workbook
	const wb = utils.book_new();

	// Combine headers and rows
	const wsData = [headers, ...rows];

	// Create worksheet
	const ws = utils.aoa_to_sheet(wsData);

	// Auto-size columns
	const colWidths = headers.map((header, index) => {
		const columnData = [header, ...rows.map((row) => row[index])];
		const maxLength = Math.max(...columnData.map((cell) => String(cell || "").length));
		return { wch: Math.min(maxLength + 2, 50) }; // Max width of 50
	});
	ws["!cols"] = colWidths;

	// Add worksheet to workbook - use Sheet1 as generic name since it's used for both clients and projects
	utils.book_append_sheet(wb, ws, "Data");

	// Write file
	writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export data to PDF format
 */
export const exportToPDF = async ({ headers, rows, filename }: ExportData) => {
	// Dynamically import jspdf-autotable to extend jsPDF
	const { default: autoTable } = await import("jspdf-autotable");

	// Create PDF document
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "mm",
		format: "a4",
	});

	// Add title
	doc.setFontSize(16);
	doc.text(filename, 14, 15);

	// Add timestamp
	doc.setFontSize(10);
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

	// Create table using autoTable function
	autoTable(doc, {
		head: [headers],
		body: rows,
		startY: 30,
		theme: "grid",
		styles: {
			fontSize: 9,
			cellPadding: 3,
		},
		headStyles: {
			fillColor: [59, 130, 246], // Blue color
			textColor: 255,
			fontStyle: "bold",
		},
		alternateRowStyles: {
			fillColor: [245, 247, 250],
		},
		columnStyles: {
			// Generic column widths for clients and projects
			0: { cellWidth: "auto" },
			1: { cellWidth: "auto" },
			2: { cellWidth: 40 },
			3: { cellWidth: 20 },
			4: { cellWidth: 15 },
			5: { cellWidth: 15 },
			6: { cellWidth: 20 },
		},
	});

	// Add page numbers
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		doc.setFontSize(9);
		doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
	}

	// Save PDF
	doc.save(`${filename}.pdf`);
};

/**
 * Export sales contract data to PDF format with specific column widths
 */
export const exportSalesContractsToPDF = async ({ headers, rows, filename }: ExportData) => {
	// Dynamically import jspdf-autotable to extend jsPDF
	const { default: autoTable } = await import("jspdf-autotable");

	// Create PDF document
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "mm",
		format: "a4",
	});

	// Add title
	doc.setFontSize(16);
	doc.text(filename, 14, 15);

	// Add timestamp
	doc.setFontSize(10);
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

	// Create table using autoTable function
	autoTable(doc, {
		head: [headers],
		body: rows,
		startY: 30,
		theme: "grid",
		styles: {
			fontSize: 9,
			cellPadding: 3,
		},
		headStyles: {
			fillColor: [59, 130, 246], // Blue color
			textColor: 255,
			fontStyle: "bold",
		},
		alternateRowStyles: {
			fillColor: [245, 247, 250],
		},
		columnStyles: {
			// Sales contracts specific column widths with category
			0: { cellWidth: 50 }, // Project (smaller to fit category)
			1: { cellWidth: 20 }, // Category
			2: { cellWidth: 30, halign: "right" }, // Monthly Recurring - right aligned
			3: { cellWidth: 30, halign: "right" }, // Contract Value - right aligned
			4: { cellWidth: 22 }, // Start Date
			5: { cellWidth: 22 }, // End Date
			6: { cellWidth: 35 }, // Salesperson
			7: { cellWidth: 18 }, // Status
		},
	});

	// Add page numbers
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		doc.setFontSize(9);
		doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
	}

	// Save PDF
	doc.save(`${filename}.pdf`);
};

/**
 * Prepare client data for export
 */
export const prepareClientDataForExport = (clients: Record<string, unknown>[]) => {
	const headers = ["Client", "Contact", "Address", "Projects", "Contracts", "Status", "Created Date"];

	const rows = clients.map((client) => [
		// Client column - merge name and company
		[client.name, client.company]
			.filter(Boolean)
			.join(" - "),
		// Contact column - merge email and phone
		[client.email, client.phone]
			.filter(Boolean)
			.join(" | "),
		client.address || "",
		client.projects_count || 0,
		client.sales_contracts_count || 0,
		client.status || "",
		client.created_at ? new Date(client.created_at).toLocaleDateString() : "",
	]);

	return { headers, rows };
};

/**
 * Prepare Bom data for export
 */
export const prepareBomDataForExport = (boms: Record<string, unknown>[], projectName = "Untitled Project") => {
	const headers = [
		"No",
		"Item",
		"Brand / Model",
		"Specifications",
		"Warranty",
		"Qty",
		"Unit",
		"Additional Costs",
		"Supplier Unit Cost",
		"Supplier Total",
		"Company Unit Cost",
		"Company Total",
	];

	const parseField = (field: unknown): Record<string, string | number> => {
		if (typeof field === "string") {
			try { return JSON.parse(field); } catch { return {}; }
		}
		return (field as Record<string, string | number>) || {};
	};

	let supplierGrandTotal = 0;
	let companyGrandTotal = 0;

	const rows = boms.map((bom, index) => {
		const specs = parseField(bom.specifications);
		const addCosts = parseField(bom.additional_costs);

		const specsText = Object.entries(specs).map(([k, v]) => `${k}: PHP ${v}`).join("\n") || "-";
		const addCostsText = Object.entries(addCosts).map(([k, v]) => `${k}: PHP ${v}`).join("\n") || "-";

		const supplierEffTotal = (Number(bom.supplier_total_cost) || 0) * (Number(bom.quantity) || 0);
		supplierGrandTotal += supplierEffTotal;
		companyGrandTotal += Number(bom.company_total_cost) || 0;

		return [
			index + 1,
			String(bom.item_name ?? ""),	// string
			String(bom.brand_model ?? ""),	// string
			specsText,	// string — already from .join()
			Number(bom.warranty ?? ""),
			Number(bom.quantity) || 0,
			String(bom.unit ?? ""),	
			addCostsText,	// string — already from .join()
			`PHP ${(Number(bom.supplier_unit_cost) || 0).toLocaleString()}`,   
			`PHP ${supplierEffTotal.toLocaleString()}`, 
			`PHP ${(Number(bom.company_unit_cost) || 0).toLocaleString()}`,
			`PHP ${(Number(bom.company_total_cost) || 0).toLocaleString()}`,
		];
	});

	const filename = `BOM_${projectName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}`;

	return { headers, rows, supplierGrandTotal: `PHP ${supplierGrandTotal.toLocaleString()}`, companyGrandTotal: `PHP ${companyGrandTotal.toLocaleString()}`, projectName, filename };
};

/**
 * Export BOM data to pdf
 */

export const buildBomPdfDoc = async ({ headers, rows, filename }: ExportData) => {
	// import jspdf for pdf
	const { default: autoTable } = await import("jspdf-autotable");

	// create pdf document
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "mm",
		format: "a4"
	});

	// add title 
	doc.setFontSize(16);
	doc.text(filename, 14, 15);

	// Add timestamp
	doc.setFontSize(10);
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

	// Create table using autotable 
	autoTable(doc, {
		head: [headers],
		body: rows,
		startY: 30,
		didParseCell: (data) => {
			// We only want to capitalize body cells, usually strings in columns 1, 2, 6, etc.
			if (data.section === 'body' && typeof data.cell.raw === 'string') {
				const text = data.cell.raw;
				// Capitalize first letter of each word (Title Case)
				data.cell.text = [text.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())];
			}
			// If this is the "Additional Costs" column (index 7)
			if (data.column.index === 7 && typeof data.cell.raw === 'object' && data.cell.raw !== null) {
				const entries = Object.entries(data.cell.raw)
					.map(([key, val]) => `${key}: ${val}`)
					.join("\n"); // Using \n creates a nice list if there are multiple costs

				data.cell.text = [entries];
			}
		},
		theme: "grid",
		styles: {
			fontSize: 8,
			cellPadding: 4,
		},
		headStyles: {
			fillColor: [59, 130, 246], // Blue color
			textColor: 255,
			fontStyle: "bold",
		},
		alternateRowStyles: {
			fillColor: [245, 247, 250],
		},
		// column width and alignment customization
		columnStyles: {
			0: { cellWidth: 12, halign: "center" }, // No.
			1: { halign: "left" },                  // Item - No width: Let it expand!
			2: { halign: "left" },                  // Brand/Model - No width: Let it expand!
			3: { halign: "left" },   // Specifications
			4: { cellWidth: 15, halign: "center" }, // Warranty
			5: { cellWidth: 12, halign: "center" }, // Qty
			6: { cellWidth: 15, halign: "center" }, // Unit
			7: { halign: "left", fontSize: 7 }, // Additional Costs (Small font helps!)
			8: { cellWidth: 22, halign: "right" },  // Supplier Unit Cost
			9: { cellWidth: 22, halign: "right" },  // Supplier Total
			10: { cellWidth: 22, halign: "right" }, // Company Unit Cost
			11: { halign: "right" }, // Company Total
		},
	});

	// Add page numbers
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		doc.setFontSize(9);
		doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
	}

	return doc;
};

export const exportBomToPdf = async (data: ExportData) => {
	const doc = await buildBomPdfDoc(data);
	doc.save(`${data.filename}.pdf`);
};

export const openBomPdfInNewTab = async (data: ExportData) => {
	const doc = await buildBomPdfDoc(data);
	const blobUrl = doc.output("bloburl");
	window.open(blobUrl, "_blank", "noopener,noreferrer");
};

/**
 * Prepare project data for export
 */
export const prepareProjectDataForExport = (projects: Record<string, unknown>[]) => {
	const headers = [
		"Project",
		"Client",
		"Description",
		"Start Date",
		"Category",
		"Status",
		"Contact Person",
		"Contact Number",
		"NOA Date",
		"NTP Date",
		"Contract Date",
		"Purchase Order",
		"Notes",
	];

	const rows = projects.map((project) => [
		project.name || "",
		// Client column - merge client company and name if different
		project.client
			? [project.client.company || project.client.name, project.client.company && project.client.name !== project.client.company ? project.client.name : null]
				.filter(Boolean)
				.join(" - ")
			: "-",
		project.description || "",
		project.date_start ? new Date(project.date_start).toLocaleDateString() : "",
		project.category === "one-time"
			? "One-time"
			: project.category === "recurring"
				? "Recurring"
				: project.category === "recurring-one-time"
					? "Recurring & One-time"
					: "",
		project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : "",
		project.contact_person || "",
		project.contact_number || "",
		project.noa_date ? new Date(project.noa_date).toLocaleDateString() : "",
		project.ntp_date ? new Date(project.ntp_date).toLocaleDateString() : "",
		project.contract_date ? new Date(project.contract_date).toLocaleDateString() : "",
		project.purchase_order || "",
		project.notes || "",
	]);

	return { headers, rows };
};

/**
 * Prepare sales contract data for export
 */
export const prepareSalesContractDataForExport = (contracts: Record<string, unknown>[]) => {
	const headers = ["Project", "Category", "Monthly Recurring", "Contract Value", "Start Date", "End Date", "Salesperson", "Status"];

	const rows = contracts.map((contract) => [
		// Project column - merge project name and client on separate lines
		[contract.project?.name || "", contract.project?.client ? contract.project.client.company || contract.project.client.name : ""]
			.filter(Boolean)
			.join("\n"),
		contract.project?.category === "one-time"
			? "One-time"
			: contract.project?.category === "recurring"
				? "Recurring"
				: contract.project?.category === "recurring-one-time"
					? "Recurring & One-time"
					: "",
		`PHP ${formatNumber(contract.monthly_recurring_sales)}`,
		`PHP ${formatNumber(contract.contract_amount)}`,
		contract.start_date ? new Date(contract.start_date).toLocaleDateString() : "",
		contract.end_date ? new Date(contract.end_date).toLocaleDateString() : "",
		contract.salesperson?.name || "",
		contract.status ? contract.status.charAt(0).toUpperCase() + contract.status.slice(1) : "",
	]);

	return { headers, rows };
};

/**
 * Prepare sales dashboard data for export
 */
export const prepareSalesDashboardDataForExport = (dashboardData: Record<string, unknown>) => {
	const headers = ["Salesperson", "Total Contracts", "Monthly Sales", "Total Contract Value", "Total Royalty Amount"];

	const rows = (dashboardData.sales_by_salesperson as Record<string, unknown>[]).map((salesperson: Record<string, unknown>) => [
		salesperson.salesperson.name,
		salesperson.total_contracts,
		`PHP ${formatNumber(salesperson.total_monthly_sales)}`,
		`PHP ${formatNumber(salesperson.total_contract_value)}`,
		`PHP ${formatNumber(salesperson.total_royalty_amount)}`,
	]);

	return { headers, rows };
};

/**
 * Export sales dashboard data to PDF format with specific column widths
 */
export const exportSalesDashboardToPDF = async ({ headers, rows, filename }: ExportData) => {
	// Dynamically import jspdf-autotable to extend jsPDF
	const { default: autoTable } = await import("jspdf-autotable");

	// Create PDF document
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "mm",
		format: "a4",
	});

	// Add title
	doc.setFontSize(16);
	doc.text(filename, 14, 15);

	// Add timestamp
	doc.setFontSize(10);
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

	// Create table using autoTable function
	autoTable(doc, {
		head: [headers],
		body: rows,
		startY: 30,
		theme: "grid",
		styles: {
			fontSize: 9,
			cellPadding: 3,
		},
		headStyles: {
			fillColor: [59, 130, 246], // Blue color
			textColor: 255,
			fontStyle: "bold",
		},
		alternateRowStyles: {
			fillColor: [245, 247, 250],
		},
		columnStyles: {
			// Sales dashboard specific column widths
			0: { cellWidth: 50 }, // Salesperson
			1: { cellWidth: 30, halign: "center" }, // Total Contracts - center aligned
			2: { cellWidth: 45, halign: "right" }, // Monthly Sales - right aligned
			3: { cellWidth: 45, halign: "right" }, // Total Contract Value - right aligned
			4: { cellWidth: 45, halign: "right" }, // Total Royalty Amount - right aligned
		},
	});

	// Add page numbers
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		doc.setFontSize(9);
		doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
	}

	// Save PDF
	doc.save(`${filename}.pdf`);
};

/**
 * Prepare monthly trend data for export
 */
export const prepareMonthlyTrendDataForExport = (monthlyTrend: Record<string, unknown>[]) => {
	const headers = ["Month", "Total Sales", "Collected", "Pending"];

	const rows = monthlyTrend.map((trend) => [
		trend.month,
		`PHP ${formatNumber(trend.sales)}`,
		`PHP ${formatNumber(trend.collected)}`,
		`PHP ${formatNumber(trend.pending)}`,
	]);

	return { headers, rows };
};

/**
 * Export monthly trend data to PDF format with specific column widths
 */
export const exportMonthlyTrendToPDF = async ({ headers, rows, filename }: ExportData) => {
	// Dynamically import jspdf-autotable to extend jsPDF
	const { default: autoTable } = await import("jspdf-autotable");

	// Create PDF document
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "mm",
		format: "a4",
	});

	// Add title
	doc.setFontSize(16);
	doc.text(filename, 14, 15);

	// Add timestamp
	doc.setFontSize(10);
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

	// Create table using autoTable function
	autoTable(doc, {
		head: [headers],
		body: rows,
		startY: 30,
		theme: "grid",
		styles: {
			fontSize: 10,
			cellPadding: 4,
		},
		headStyles: {
			fillColor: [59, 130, 246], // Blue color
			textColor: 255,
			fontStyle: "bold",
		},
		alternateRowStyles: {
			fillColor: [245, 247, 250],
		},
		columnStyles: {
			// Monthly trend specific column widths
			0: { cellWidth: 50, halign: "left" }, // Month - left aligned
			1: { cellWidth: 50, halign: "right" }, // Total Sales - right aligned
			2: { cellWidth: 50, halign: "right" }, // Collected - right aligned
			3: { cellWidth: 50, halign: "right" }, // Pending - right aligned
		},
	});

	// Add page numbers
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		doc.setFontSize(9);
		doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
	}

	// Save PDF
	doc.save(`${filename}.pdf`);
};

/**
 * Prepare collection report data for export
 */
export const prepareCollectionReportDataForExport = (collectionData: Record<string, unknown>[], year: number) => {
	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	const headers = ["Client / Project", "Salesperson", "Contract Amount", "Monthly Recurring", ...months];

	const rows = collectionData.map((contract) => [
		// Merge client and project with project on new line
		`${contract.client_name}\n${contract.project_name}`,
		contract.salesperson_name,
		`PHP ${formatNumber(contract.contract_amount)}`,
		`PHP ${formatNumber(contract.monthly_recurring_sales)}`,
		...months.map((_, index) => {
			const monthKey = (index + 1).toString().padStart(2, "0");
			const status = contract.monthly_status[monthKey];
			switch (status) {
				case "collected":
					return "/";
				case "pending":
					return "X";
				default:
					return "-";
			}
		}),
	]);

	return { headers, rows };
};

/**
 * Export collection report data to PDF format with specific column widths
 */
export const exportCollectionReportToPDF = async ({ headers, rows, filename }: ExportData) => {
	// Dynamically import jspdf-autotable to extend jsPDF
	const { default: autoTable } = await import("jspdf-autotable");

	// Create PDF document in landscape for better month columns visibility
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "mm",
		format: "a4",
	});

	// Add title
	doc.setFontSize(16);
	doc.text(filename, 14, 15);

	// Add timestamp
	doc.setFontSize(10);
	doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

	// Create table using autoTable function
	autoTable(doc, {
		head: [headers],
		body: rows,
		startY: 30,
		theme: "grid",
		styles: {
			fontSize: 8,
			cellPadding: 2,
		},
		headStyles: {
			fillColor: [59, 130, 246], // Blue color
			textColor: 255,
			fontStyle: "bold",
		},
		alternateRowStyles: {
			fillColor: [245, 247, 250],
		},
		columnStyles: {
			// Collection report specific column widths
			0: { cellWidth: 60 }, // Client / Project (merged, wider)
			1: { cellWidth: 30 }, // Salesperson
			2: { cellWidth: 25, halign: "right" }, // Contract Amount - right aligned
			3: { cellWidth: 25, halign: "right" }, // Monthly Recurring - right aligned
			// Month columns (4-15) - small and centered
			...Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i + 4, { cellWidth: 12, halign: "center" }])),
		},
	});

	// Add page numbers
	const pageCount = doc.getNumberOfPages();
	for (let i = 1; i <= pageCount; i++) {
		doc.setPage(i);
		doc.setFontSize(9);
		doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
	}

	// Save PDF
	doc.save(`${filename}.pdf`);
};
